import './index.scss'
import { Controller as BaseController } from 'stimulus'

export class Controller extends BaseController {
  static targets = ['text']

  static values = {
    message: String,
    sticky: Boolean
  }

  animationEndEventName = this.getAnimationEndEventName();

  /**
   * Identify which animation event is available in the browser.
   * @author Javascript Jeep🚙💨
   * @link https://medium.com/better-programming/detecting-the-end-of-css-transition-events-in-javascript-8653ae230dc7
   */
  getAnimationEndEventName() {
    const animations = {
      animation: 'animationend',
      OAnimation: 'oAnimationEnd',
      MozAnimation: 'animationend',
      WebkitAnimation: 'webkitAnimationEnd'
    }
    const bodyStyle = document.body.style
    for (const animation in animations) {
      if (bodyStyle[animation] !== undefined) {
        return animations[animation]
      }
    }
  }

  /**
   * Have the toast do things as soon as it's attached to the DOM
   * @link https://stimulus.hotwire.dev/reference/lifecycle-callbacks#connection
   */
  connect() {
    // Determine where to place the next notification based on current notifications
    this.positionNotification()

    // Change the live region content, only after it's been attached.
    // This ensure the notification gets announced by assistive technologies.
    // @link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
    this.textTarget.textContent = this.messageValue

    if (!this.stickyValue) {
      // Set it up so that the toast will be removed from the DOM, after it's faded out
      this.element.addEventListener(this.animationEndEventName, (event) => {
        this.handleNotificationBehavior(this.identifier, event)
      })
    }
  }

  /**
   * Fade the toast notification on- and off-screen
   * by toggling a specific CSS class that corresponds to the animation.
   * @param {string} id - The component's identifier
   * @param {AnimationEvent} event - The animation end event.
   * @see ./index.scss
   */
  handleNotificationBehavior(id, event) {
    const element = event.target
    element.classList.add(`c-${id}-fade-out`)
    if (event.animationName.includes('fade-out')) {
      element.parentNode.removeChild(element)
    }
  }

  /**
   * Set the css `top` property based on currently displayed notifications
   * TODO: Create a ToastNotificationsList::Component, and have it handle this functionality
   * (that way, rather than getting notifications by calling document.getElementsByClassName,
   * we can have notificationTargets
   */
  positionNotification() {
    // Collect all of the notifications in the DOM
    const notifications = document.getElementsByClassName(`c-${this.identifier}-container`)

    // Filter collection so only already-positioned notifications remain
    const positionedNotifications = Array.from(notifications).filter(
      notification => notification.dataset.positioned === 'true'
    )

    // The first notification has zero offsets
    let lowestOffset = 0
    let heightOffset = 0

    // Use default offsets for the first notification
    if (positionedNotifications.length >= 1) {
      positionedNotifications.forEach(notification => {
        const top = parseInt(getComputedStyle(notification).top)
        if (top > lowestOffset) {
          heightOffset = notification.offsetHeight
          lowestOffset = top
        }
      })
    }

    // Position notification
    const offset = 8 + lowestOffset + heightOffset
    this.element.style.top = `${offset}px`

    // Allows us to know which notifications have been positioned
    this.element.dataset.positioned = true
  }

  /**
   * Remove this component instance from the DOM,
   * when the turbolinks:before-cache event has fired.
   * @see javascript/packs/application.js
   * @link https://github.com/hotwired/stimulus/issues/104#issuecomment-365393601
   */
  teardown() {
    this.element.parentNode.removeChild(this.element)
  }
}
